import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  Output,
  EventEmitter,
  inject
} from "@angular/core";
import { NgClass } from "@angular/common";
import { VisitStore } from "@app/services/store/visit-store";
import { Visit_DetailProjection } from "@app/models/rest/visit";
import { MomentModule } from "ngx-moment";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import moment, { Moment } from "moment";

@Component({
  selector: "app-visit-property-list-component",
  templateUrl: "./visit-property-list-component.component.html",
  styleUrls: ["./visit-property-list-component.component.css"],
  standalone: true,
  imports: [MomentModule, MatProgressSpinner, NgClass, MatIcon, MatTooltip]
})
export class VisitPropertyListComponentComponent implements OnChanges, OnInit {
  visitStore = inject(VisitStore);

  @Input()
  public propertyId: string;

  @Input()
  public selectedDate: Moment;

  public visits: Visit_DetailProjection[];
  public visitsBeforeCurrentWeek: Visit_DetailProjection[] = [];
  public visitsInCurrentWeek: Visit_DetailProjection[] = [];
  public visitsAfterCurrentWeek: Visit_DetailProjection[] = [];

  @Output()
  public hasVisitsBeforeWeek = new EventEmitter<boolean>();

  @Output()
  public hasVisitsAfterWeek = new EventEmitter<boolean>();

  @Output()
  public visitsInWeek = new EventEmitter<Visit_DetailProjection[]>();

  loadVisits(): void {
    this.visitStore
      .advancedSearchObservable(
        {
          pageParams: {
            page: 0,
            size: 500
          },
          params: {
            projection: "detail"
          },
          sort: {
            appointmentStart: "DESC"
          }
        },
        [
          {
            key: "job.externalPropertyReference",
            operation: "Equals",
            value: this.propertyId
          },
          {
            key: "visitStatus",
            operation: "Contains",
            value: ["BOOKED", "FLOATING"]
          },
          {
            key: "appointmentStart",
            operation: "GreaterThan",
            value: moment().add(-1, "days").toDate()
          }
        ]
      )
      .subscribe((value) => {
        if (!value || !value.resources) {
          this.visits = [];
        } else {
          this.visits = value.resources as unknown as Visit_DetailProjection[];
          // Call categorizeVisitsByWeek after visits are loaded
          this.categorizeVisitsByWeek();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["selectedDate"]) {
      // Always categorize when selected date changes (even on first change)
      console.log("Date changed, categorizing visits", this.selectedDate);
      setTimeout(() => this.categorizeVisitsByWeek(), 0);
    }

    if (changes["propertyId"]) {
      this.loadVisits();
    }
  }

  ngOnInit(): void {
    this.loadVisits();
  }

  /**
   * Categorizes visits based on their relation to the current selected week
   */
  categorizeVisitsByWeek(): void {
    if (!this.visits || !this.selectedDate) return;

    const weekStart = moment(this.selectedDate).startOf("week");
    const weekEnd = moment(this.selectedDate).endOf("week");

    this.visitsBeforeCurrentWeek = [];
    this.visitsInCurrentWeek = [];
    this.visitsAfterCurrentWeek = [];

    for (const visit of this.visits) {
      const appointmentStart = moment(visit.appointmentStart);

      if (appointmentStart.isBefore(weekStart)) {
        this.visitsBeforeCurrentWeek.push(visit);
      } else if (appointmentStart.isAfter(weekEnd)) {
        this.visitsAfterCurrentWeek.push(visit);
      } else {
        this.visitsInCurrentWeek.push(visit);
      }
    }

    // Emit events with the results
    this.hasVisitsBeforeWeek.emit(this.visitsBeforeCurrentWeek.length > 0);
    this.hasVisitsAfterWeek.emit(this.visitsAfterCurrentWeek.length > 0);
    this.visitsInWeek.emit(this.visitsInCurrentWeek);
  }

  /**
   * Gets day of week (0-6) and time info for a specific visit
   * Returns info needed to match with available slots
   */
  getVisitSlotInfo(visit: Visit_DetailProjection): {
    dayOfWeek: number;
    startHour: number;
    startMinute: number;
  } {
    if (!visit || !visit.appointmentStart) return null;

    const appointmentStart = moment(visit.appointmentStart);
    const dayOfWeek = appointmentStart.day();
    const startHour = appointmentStart.hour();
    const startMinute = appointmentStart.minute();

    return { dayOfWeek, startHour, startMinute };
  }
}
